import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { convertDate, shortenString } from 'src/components/Table/helpers';
import { formatKycStatusLabel } from 'src/api/adminUsers';
import { formatBigNumber } from 'src/utils/constants';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

type PresaleUserDetailModalProps = {
  user: Record<string, any>;
  onClose: () => void;
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;

const pick = (...values: unknown[]) =>
  values.find((v) => v !== undefined && v !== null && v !== '') as string | number | undefined;


const formatAmount = (value: unknown) => {
  if (value == null || value === '') return null;
  try {
    return formatBigNumber(value as any);
  } catch {
    return String(value);
  }
};

const PresaleUserDetailModal: React.FC<PresaleUserDetailModalProps> = ({ user, onClose }) => {
  const navigate = useNavigate();

  const userId = String(pick(user.user_id, user.id, user._id) ?? '');
  const name = user.name ? String(user.name) : '—';
  const email = user.email ? String(user.email) : '';
  const kycStatus = user.kyc_status ? String(user.kyc_status) : '';
  const bxlendId = user.bxlend_id ? String(user.bxlend_id) : '';
  const receivingWallet = user.receiving_wallet ? String(user.receiving_wallet) : '';
  const referralAccount = user.referral_account ? String(user.referral_account) : '';
  const createdAt = user.created_at as string | undefined;

  const referralReward = formatAmount(user.referral_reward);
  const totalAllocation = formatAmount(user.total_allocation);
  const pendingAllocation = formatAmount(user.pending_allocation);

  const createdMeta = createdAt
    ? `Joined ${convertDate(createdAt).date}${
        convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''
      }`
    : null;

  return (
    <Modal isOpen onClose={onClose}>
      <Breadcrumb>Pre-sale users{userId ? ` / ${shortenString(userId)}` : ''}</Breadcrumb>

      <HeadlineRow>
        <TitleGroup>
          <Title>
            {name !== '—' ? name : email || 'Pre-sale user'}
            {kycStatus ? (
              <StatusBadge $status={kycStatus}>{formatKycStatusLabel(kycStatus)}</StatusBadge>
            ) : null}
          </Title>
          {createdMeta ? <MetaLine>{createdMeta}</MetaLine> : null}
        </TitleGroup>
      </HeadlineRow>

      <Card>
        <CardTitle>User</CardTitle>
        <DetailGrid>
          {userId ? (
            <DetailItem>
              <DetailLabel>User</DetailLabel>
              <DetailValue title={email ? `${name} (${email})` : name}>
                <UserLink
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`${ROUTE_USERS}/${userId}`);
                  }}
                >
                  {name !== '—' ? name : shortenString(userId)}
                </UserLink>
              </DetailValue>
            </DetailItem>
          ) : (
            <DetailItem>
              <DetailLabel>Name</DetailLabel>
              <DetailValue $muted={name === '—'}>{name}</DetailValue>
            </DetailItem>
          )}
          <DetailItem>
            <DetailLabel>Email</DetailLabel>
            <DetailValue $muted={!email}>{email || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>BxLend ID</DetailLabel>
            <DetailValue $muted={!bxlendId}>{bxlendId || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>KYC status</DetailLabel>
            <DetailValue>
              {kycStatus ? (
                <StatusBadge $status={kycStatus}>{formatKycStatusLabel(kycStatus)}</StatusBadge>
              ) : (
                '—'
              )}
            </DetailValue>
          </DetailItem>
          {referralAccount ? (
            <DetailItem>
              <DetailLabel>Referral account</DetailLabel>
              <DetailValue>{referralAccount}</DetailValue>
            </DetailItem>
          ) : null}
          {receivingWallet ? (
            <DetailItem>
              <DetailLabel>Receiving wallet</DetailLabel>
              <CopyableValue value={receivingWallet} label="Receiving wallet" />
            </DetailItem>
          ) : null}
          {createdAt ? (
            <DetailItem>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>
                {convertDate(createdAt).date}
                {convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
        </DetailGrid>
      </Card>

      <SectionSpacer />

      <Card>
        <CardTitle>Allocation</CardTitle>
        <DetailGrid>
          <DetailItem>
            <DetailLabel>Referral reward</DetailLabel>
            <DetailValue $muted={!referralReward}>{referralReward || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Total allocation</DetailLabel>
            <DetailValue $muted={!totalAllocation}>{totalAllocation || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Pending allocation</DetailLabel>
            <DetailValue $muted={!pendingAllocation}>{pendingAllocation || '—'}</DetailValue>
          </DetailItem>
        </DetailGrid>
      </Card>
    </Modal>
  );
};

export default PresaleUserDetailModal;
